import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getStatistiquesComptables } from '../services/comptabiliteService';

const emptyStats = {
  periode: { date_debut: null, date_fin: null },
  resume: {
    total_encaisse: 0,
    encaisse_supprime: 0,
    nombre_paiements: 0,
    panier_moyen: 0,
    volume_locatif_estime: 0,
    souscriptions_actives: 0,
    souscriptions_supprimees: 0,
    preuves_manquantes: 0,
    livrables_manquants: 0
  },
  par_statut: [],
  par_mode_paiement: [],
  par_mois: [],
  par_destination: [],
  alertes: { preuves_manquantes: [], livrables_manquants: [] },
  paiements_recents: []
};

const initialSectionPages = {
  months: 1,
  alerts: 1,
  statuses: 1,
  paymentModes: 1,
  destinations: 1,
  recentPayments: 1
};

const pageSizes = {
  months: 6,
  alerts: 4,
  statuses: 5,
  paymentModes: 5,
  destinations: 5,
  recentPayments: 10
};

const statusLabels = {
  BROUILLON: 'Brouillon',
  EN_ATTENTE_PAIEMENT: 'Attente paiement',
  PAYEE: 'Payée',
  ATTENTE_LIVRAISON: 'Attente livraison',
  LIVRE: 'Livrée',
  CLOTURE: 'Clôturée',
  ANNULEE: 'Annulée',
  SUPPRIMEE: 'Supprimée'
};

const statusClasses = {
  BROUILLON: 'bg-gray-100 text-gray-700',
  EN_ATTENTE_PAIEMENT: 'bg-amber-50 text-amber-700',
  PAYEE: 'bg-blue-50 text-blue-700',
  ATTENTE_LIVRAISON: 'bg-orange-50 text-orange-700',
  LIVRE: 'bg-emerald-50 text-emerald-700',
  CLOTURE: 'bg-slate-100 text-slate-700',
  ANNULEE: 'bg-red-50 text-red-700',
  SUPPRIMEE: 'bg-gray-100 text-gray-500'
};

const providerLabels = {
  manuel: 'Manuel',
  stripe: 'Stripe',
  cash: 'Espèces',
  virement: 'Virement',
  mobile_money: 'Mobile Money'
};

const formatCurrency = (amount) => new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2
}).format(Number(amount || 0));

const formatNumber = (value) => new Intl.NumberFormat('fr-FR').format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('fr-FR').format(date);
};

const formatMonth = (value) => {
  if (!value || value === 'Non date') return 'Non daté';
  const date = new Date(value + '-01T00:00:00');
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('fr-FR', { month: 'short', year: 'numeric' }).format(date);
};

const getStatusLabel = (status) => statusLabels[status] || status || '-';
const getStatusClass = (status) => statusClasses[status] || 'bg-gray-100 text-gray-700';
const getProviderLabel = (provider) => providerLabels[provider] || provider || 'Non renseigné';

const getTotalPages = (itemsCount, pageSize) => Math.max(1, Math.ceil(itemsCount / pageSize));

const getPagedItems = (items, page, pageSize) => {
  const currentPage = Math.min(Math.max(page, 1), getTotalPages(items.length, pageSize));
  const start = (currentPage - 1) * pageSize;
  return items.slice(start, start + pageSize);
};

const getPageRange = (itemsCount, page, pageSize) => {
  if (itemsCount === 0) return '0 sur 0';
  const currentPage = Math.min(Math.max(page, 1), getTotalPages(itemsCount, pageSize));
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, itemsCount);
  return start + '-' + end + ' sur ' + itemsCount;
};

const MetricCard = ({ label, value, detail, tone = 'blue' }) => {
  const tones = {
    blue: 'border-blue-100 bg-blue-50 text-blue-700',
    orange: 'border-orange-100 bg-orange-50 text-orange-700',
    slate: 'border-slate-100 bg-slate-50 text-slate-700',
    green: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    red: 'border-red-100 bg-red-50 text-red-700'
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-500 truncate">{label}</p>
          <p className="mt-2 text-2xl font-bold text-gray-900 break-words">{value}</p>
          {detail && <p className="mt-2 text-sm text-gray-500">{detail}</p>}
        </div>
        <div className={'w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 ' + (tones[tone] || tones.blue)}>
          <span className="text-sm font-semibold">{tone === 'orange' ? '!' : tone === 'green' ? '+' : tone === 'red' ? '!' : '='}</span>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div className="py-8 text-center text-sm text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
    {message}
  </div>
);

const PaginationControls = ({ page, totalItems, pageSize, onPageChange }) => {
  const totalPages = getTotalPages(totalItems, pageSize);
  const currentPage = Math.min(Math.max(page, 1), totalPages);

  if (totalItems <= pageSize) {
    return totalItems > 0 ? (
      <div className="mt-4 text-xs text-gray-500 text-right">{formatNumber(totalItems)} élément(s)</div>
    ) : null;
  }

  return (
    <div className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
      <span className="text-xs text-gray-500">{getPageRange(totalItems, currentPage, pageSize)}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Préc.
        </button>
        <span className="text-xs text-gray-500 whitespace-nowrap">{currentPage}/{totalPages}</span>
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Suiv.
        </button>
      </div>
    </div>
  );
};

const SectionCard = ({ title, subtitle, children, footer, className = '' }) => (
  <div className={'bg-white border border-gray-200 rounded-lg shadow-sm p-5 ' + className}>
    <div className="mb-5">
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
    {children}
    {footer}
  </div>
);

const ComptabiliteDashboard = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({ date_debut: '', date_fin: '' });
  const [appliedFilters, setAppliedFilters] = useState({ date_debut: '', date_fin: '' });
  const [sectionPages, setSectionPages] = useState(initialSectionPages);
  const [stats, setStats] = useState(emptyStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const setSectionPage = useCallback((section, page) => {
    setSectionPages((current) => ({ ...current, [section]: Math.max(1, page) }));
  }, []);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getStatistiquesComptables(appliedFilters);
      setStats({
        ...emptyStats,
        ...data,
        resume: { ...emptyStats.resume, ...(data.resume || {}) },
        alertes: { ...emptyStats.alertes, ...(data.alertes || {}) }
      });
    } catch (apiError) {
      const detail = apiError.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Impossible de charger les statistiques comptables.');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    setSectionPages(initialSectionPages);
  }, [appliedFilters]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const monthItems = useMemo(() => stats.par_mois || [], [stats.par_mois]);
  const statusItems = useMemo(() => (stats.par_statut || []).filter((item) => item.nombre > 0), [stats.par_statut]);
  const paymentModeItems = useMemo(() => stats.par_mode_paiement || [], [stats.par_mode_paiement]);
  const destinationItems = useMemo(() => stats.par_destination || [], [stats.par_destination]);
  const recentPaymentItems = useMemo(() => stats.paiements_recents || [], [stats.paiements_recents]);

  const alertItems = useMemo(() => {
    const proofAlerts = (stats.alertes?.preuves_manquantes || []).map((item) => ({
      ...item,
      type: 'preuve',
      key: 'preuve-' + item.id,
      label: 'Preuve manquante',
      toneClass: 'border-orange-100 text-orange-700'
    }));
    const documentAlerts = (stats.alertes?.livrables_manquants || []).map((item) => ({
      ...item,
      type: 'livrable',
      key: 'livrable-' + item.id,
      label: 'Livrable manquant',
      toneClass: 'border-blue-100 text-blue-700'
    }));
    return [...proofAlerts, ...documentAlerts];
  }, [stats.alertes]);

  const maxMonthAmount = useMemo(() => {
    return Math.max(...monthItems.map((item) => Number(item.montant || 0)), 0);
  }, [monthItems]);

  const currentPages = {
    months: Math.min(sectionPages.months, getTotalPages(monthItems.length, pageSizes.months)),
    alerts: Math.min(sectionPages.alerts, getTotalPages(alertItems.length, pageSizes.alerts)),
    statuses: Math.min(sectionPages.statuses, getTotalPages(statusItems.length, pageSizes.statuses)),
    paymentModes: Math.min(sectionPages.paymentModes, getTotalPages(paymentModeItems.length, pageSizes.paymentModes)),
    destinations: Math.min(sectionPages.destinations, getTotalPages(destinationItems.length, pageSizes.destinations)),
    recentPayments: Math.min(sectionPages.recentPayments, getTotalPages(recentPaymentItems.length, pageSizes.recentPayments))
  };

  if (user?.role !== 'admin-generale') {
    return <Navigate to="/admin-dashboard" replace />;
  }

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    const resetFilters = { date_debut: '', date_fin: '' };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
  };

  const periodeText = stats.periode?.date_debut || stats.periode?.date_fin
    ? 'Période du ' + formatDate(stats.periode?.date_debut) + ' au ' + formatDate(stats.periode?.date_fin)
    : 'Toutes les dates de versement';

  const pagedMonths = getPagedItems(monthItems, currentPages.months, pageSizes.months);
  const pagedAlerts = getPagedItems(alertItems, currentPages.alerts, pageSizes.alerts);
  const pagedStatuses = getPagedItems(statusItems, currentPages.statuses, pageSizes.statuses);
  const pagedPaymentModes = getPagedItems(paymentModeItems, currentPages.paymentModes, pageSizes.paymentModes);
  const pagedDestinations = getPagedItems(destinationItems, currentPages.destinations, pageSizes.destinations);
  const pagedRecentPayments = getPagedItems(recentPaymentItems, currentPages.recentPayments, pageSizes.recentPayments);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <div>
            <p className="text-sm font-semibold text-orange-600 uppercase tracking-wide">Administration générale</p>
            <h1 className="text-3xl font-bold text-gray-900 mt-1">Statistiques comptables</h1>
            <p className="text-gray-600 mt-2">Vue financière consolidée des souscriptions, paiements et alertes de suivi.</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <label className="block">
                <span className="text-xs font-medium text-gray-500">Date début</span>
                <input
                  type="date"
                  name="date_debut"
                  value={filters.date_debut}
                  onChange={handleFilterChange}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-500">Date fin</span>
                <input
                  type="date"
                  name="date_fin"
                  value={filters.date_fin}
                  onChange={handleFilterChange}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <div className="flex gap-2">
                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                  Filtrer
                </button>
                <button type="button" onClick={handleReset} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                  Reset
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-gray-500">{periodeText}</p>
          <button onClick={loadStats} className="self-start sm:self-auto px-4 py-2 border border-blue-200 text-blue-700 bg-blue-50 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors">
            Actualiser
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-10 text-center shadow-sm">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-gray-600">Chargement des statistiques...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
              <MetricCard label="Encaissements actifs" value={formatCurrency(stats.resume.total_encaisse)} detail={formatNumber(stats.resume.nombre_paiements) + ' paiements'} tone="blue" />
              <MetricCard label="Panier moyen" value={formatCurrency(stats.resume.panier_moyen)} detail="Sur paiements non supprimés" tone="green" />
              <MetricCard label="Volume locatif estimé" value={formatCurrency(stats.resume.volume_locatif_estime)} detail={formatNumber(stats.resume.souscriptions_actives) + ' souscriptions actives'} tone="slate" />
              <MetricCard label="Montant supprimé" value={formatCurrency(stats.resume.encaisse_supprime)} detail={formatNumber(stats.resume.souscriptions_supprimees) + ' souscriptions supprimées'} tone="orange" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <SectionCard title="Encaissements par mois" subtitle="Basé sur la date de versement." className="lg:col-span-2">
                {monthItems.length === 0 ? (
                  <EmptyState message="Aucun paiement sur cette période." />
                ) : (
                  <>
                    <div className="space-y-4 max-h-80 overflow-y-auto pr-1">
                      {pagedMonths.map((item) => {
                        const percentage = maxMonthAmount > 0 ? Math.max(6, Math.round((Number(item.montant || 0) / maxMonthAmount) * 100)) : 6;
                        return (
                          <div key={item.mois}>
                            <div className="flex items-center justify-between text-sm mb-1 gap-3">
                              <span className="font-medium text-gray-700">{formatMonth(item.mois)}</span>
                              <span className="text-gray-600 text-right">{formatCurrency(item.montant)} · {formatNumber(item.nombre)} paiement(s)</span>
                            </div>
                            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-600 rounded-full" style={{ width: String(percentage) + '%' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <PaginationControls
                      page={currentPages.months}
                      totalItems={monthItems.length}
                      pageSize={pageSizes.months}
                      onPageChange={(page) => setSectionPage('months', page)}
                    />
                  </>
                )}
              </SectionCard>

              <SectionCard title="Alertes de suivi" subtitle="Contrôles documentaires côté finance.">
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div className="border border-orange-100 bg-orange-50 rounded-lg p-4">
                    <p className="text-xs font-medium text-orange-700">Preuves manquantes</p>
                    <p className="text-2xl font-bold text-orange-800 mt-1">{formatNumber(stats.resume.preuves_manquantes)}</p>
                  </div>
                  <div className="border border-blue-100 bg-blue-50 rounded-lg p-4">
                    <p className="text-xs font-medium text-blue-700">Livrables manquants</p>
                    <p className="text-2xl font-bold text-blue-800 mt-1">{formatNumber(stats.resume.livrables_manquants)}</p>
                  </div>
                </div>
                {alertItems.length === 0 ? (
                  <EmptyState message="Aucune alerte prioritaire." />
                ) : (
                  <>
                    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                      {pagedAlerts.map((item) => (
                        <div key={item.key} className={'border rounded-lg p-3 ' + item.toneClass}>
                          <p className="text-sm font-medium text-gray-900 truncate">{item.client || item.reference}</p>
                          <p className="text-xs mt-1">
                            {item.type === 'preuve'
                              ? item.label + ' · ' + formatCurrency(item.montant_verse)
                              : item.label + ' · ' + getStatusLabel(item.statut)}
                          </p>
                        </div>
                      ))}
                    </div>
                    <PaginationControls
                      page={currentPages.alerts}
                      totalItems={alertItems.length}
                      pageSize={pageSizes.alerts}
                      onPageChange={(page) => setSectionPage('alerts', page)}
                    />
                  </>
                )}
              </SectionCard>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
              <SectionCard title="Répartition par statut">
                {statusItems.length === 0 ? (
                  <EmptyState message="Aucune souscription." />
                ) : (
                  <>
                    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                      {pagedStatuses.map((item) => (
                        <div key={item.statut} className="flex items-center justify-between gap-3">
                          <span className={'px-2.5 py-1 rounded-full text-xs font-medium ' + getStatusClass(item.statut)}>{getStatusLabel(item.statut)}</span>
                          <span className="text-sm text-gray-600 text-right">{formatNumber(item.nombre)} · {formatCurrency(item.montant_verse)}</span>
                        </div>
                      ))}
                    </div>
                    <PaginationControls
                      page={currentPages.statuses}
                      totalItems={statusItems.length}
                      pageSize={pageSizes.statuses}
                      onPageChange={(page) => setSectionPage('statuses', page)}
                    />
                  </>
                )}
              </SectionCard>

              <SectionCard title="Modes de paiement">
                {paymentModeItems.length === 0 ? (
                  <EmptyState message="Aucun paiement comptabilisé." />
                ) : (
                  <>
                    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                      {pagedPaymentModes.map((item) => (
                        <div key={item.mode} className="flex items-center justify-between gap-3 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{getProviderLabel(item.mode)}</p>
                            <p className="text-xs text-gray-500">{formatNumber(item.nombre)} paiement(s)</p>
                          </div>
                          <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(item.montant)}</p>
                        </div>
                      ))}
                    </div>
                    <PaginationControls
                      page={currentPages.paymentModes}
                      totalItems={paymentModeItems.length}
                      pageSize={pageSizes.paymentModes}
                      onPageChange={(page) => setSectionPage('paymentModes', page)}
                    />
                  </>
                )}
              </SectionCard>

              <SectionCard title="Destinations principales">
                {destinationItems.length === 0 ? (
                  <EmptyState message="Aucune destination disponible." />
                ) : (
                  <>
                    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                      {pagedDestinations.map((item) => (
                        <div key={item.pays} className="flex items-center justify-between gap-3 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{item.pays}</p>
                            <p className="text-xs text-gray-500">{formatNumber(item.nombre)} souscription(s)</p>
                          </div>
                          <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(item.montant)}</p>
                        </div>
                      ))}
                    </div>
                    <PaginationControls
                      page={currentPages.destinations}
                      totalItems={destinationItems.length}
                      pageSize={pageSizes.destinations}
                      onPageChange={(page) => setSectionPage('destinations', page)}
                    />
                  </>
                )}
              </SectionCard>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Paiements récents</h2>
                <p className="text-sm text-gray-500 mt-1">Dernières lignes comptabilisées hors souscriptions supprimées.</p>
              </div>
              {recentPaymentItems.length === 0 ? (
                <div className="p-5"><EmptyState message="Aucun paiement récent." /></div>
              ) : (
                <>
                  <div className="max-h-96 overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Référence</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Mode</th>
                          <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Montant</th>
                          <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {pagedRecentPayments.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-5 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">{item.reference || '-'}</td>
                            <td className="px-5 py-4 text-sm text-gray-600 whitespace-nowrap">{item.client || '-'}</td>
                            <td className="px-5 py-4 whitespace-nowrap"><span className={'px-2.5 py-1 rounded-full text-xs font-medium ' + getStatusClass(item.statut)}>{getStatusLabel(item.statut)}</span></td>
                            <td className="px-5 py-4 text-sm text-gray-600 whitespace-nowrap">{getProviderLabel(item.mode)}</td>
                            <td className="px-5 py-4 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">{formatCurrency(item.montant_verse)}</td>
                            <td className="px-5 py-4 text-sm text-gray-600 text-right whitespace-nowrap">{formatDate(item.date_versement)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-5 pb-4">
                    <PaginationControls
                      page={currentPages.recentPayments}
                      totalItems={recentPaymentItems.length}
                      pageSize={pageSizes.recentPayments}
                      onPageChange={(page) => setSectionPage('recentPayments', page)}
                    />
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ComptabiliteDashboard;
